import config from "@config";
import { all, call, put, takeEvery, takeLatest, select, spawn } from "redux-saga/effects";
import { push } from "connected-react-router";

import { RECORDING_PATH } from "@routes/constants";
import serviceActionCreator from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import * as actions from "@modules/recording/constants/actions";
import actionCreator from "@modules/recording/actionCreator";
import { getUserType } from "@modules/account/selectors";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { USER_TYPES } from "@modules/account/constants";
import {
  selectClientId,
  selectCallInteraction,
  getCallPurpose,
  selectWizardState,
  getClient,
} from "@modules/recording/selectors";
import http from "@http";
import type { Employee } from "@modules/recording/types";
import triage from "./triage";
import nurseTransferSagas from "./nurseTransfer";
import clientInformation from "./clientInformation";
import { parseSchema } from "@utils/schema";
import moment from "moment";

const delay = ms => new Promise(res => setTimeout(res, ms));

type onGetPreselectedEmployeePayload = $Exact<{
  clientId: string,
  employeeId: string,
  callerIdentity?: string,
  reasonForCall?: string,
}>;

export function* onGetPreselectedEmployee({
  payload,
}: {
  payload: onGetPreselectedEmployeePayload,
}) {
  try {
    if (!payload.clientId || !payload.employeeId) {
      throw new Error("Invalid payload");
    }
    const callerIdentity = "SELF";
    const reasonForCall = "START_UPDATE_CLOSE_ABSENCE";

    const employeeResponse = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}`,
    );
    const client = yield call(onSelectClient, {
      payload: { clientId: payload.clientId, noSubmit: true },
    });

    yield all([
      put(actionCreator.SetEmployee(employeeResponse.data)),
      put(
        actionCreator.SetCallPurpose({
          callerIdentity,
          reasonForCall,
          noSubmit: true,
        }),
      ),
    ]);

    yield put(
      actionCreator.SetAbsenceRecordingWizardState([
        {},
        { client },
        {},
        { callerIdentity, reasonForCall },
      ]),
    );

    yield put(actionCreator.SetAutoIdDataPrefilled(true));
  } catch (err) {
    yield put(actionCreator.GetPreselectedEmployeeError(err.response));
  }
}

type SelectEmployeePayload = $Exact<{
  employee: Employee,
  callerIdentity?: string,
}>;

export function* onAbsenceRecordingSelectEmployee({ payload }: { payload: SelectEmployeePayload }) {
  const { employee, callerIdentity } = payload;
  const client: Object = yield select(getClient);
  const clientId: number = employee?.client?.id || client.id;
  const employeeId: number = employee.id;
  const callPurpose = yield select(getCallPurpose);
  const userType = yield select(getUserType);

  if (callerIdentity) {
    yield put(actionCreator.SetCallPurposeSuccess({ ...callPurpose, callerIdentity }));
  }

  const [employeeNotifications, hierarchyNotifications, employeeDetails] = yield all([
    call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/notification/recipient/individual`,
      {
        params: {
          limit: config.results100PerPage,
        },
      },
    ),
    call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/notification/recipient/hierarchy`,
      {
        params: {
          limit: config.results100PerPage,
        },
      },
    ),
    call(http.get, `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}`),
  ]);

  const notifications = [
    ...employeeNotifications.data.content,
    ...hierarchyNotifications.data.content,
  ].map(notification => ({
    id: notification.id,
    recipient: notification.recipient.name,
    notificationType: notification.notificationType,
  }));

  yield all([
    put(actionCreator.SetEmployeeRecordNotifications(notifications)),
    put(actionCreator.GetEmployeeAbsenceRecording({ clientId, employeeId })),
    put(actionCreator.SetEmployee(employeeDetails.data)),
    put(
      actionCreator.CreateCallInteraction({
        clientId,
        employeeId,
        formData: {
          employeeId,
          callerIdentity: callerIdentity || callPurpose?.callerIdentity,
          reasonForCall: callPurpose.reasonForCall,
          interactionSource: "INBOUND",
        },
      }),
    ),
    put(actionCreator.GetJobDuties({ clientId, employeeId })),
  ]);

  if (userType !== USER_TYPES.ABSENCE_ADVISOR) {
    yield put(
      actionCreator.GetMedicalTriageHistory({
        clientId,
        employeeId,
      }),
    );
  }

  yield put(
    actionCreator.SubmitCurrentStep({
      employee,
    }),
  );
}

type SelectClientPayload = $Exact<{
  clientId: number,
  noSubmit?: boolean,
}>;

export function* onSelectClient({ payload }: { payload: SelectClientPayload }) {
  const clientId = typeof payload.clientId === "object" ? payload.clientId.id : payload.clientId;

  const response = yield call(
    http.get,
    `/api/v2/service-admin/client/${clientId}/service-config/platform/data-security-employee-identity`,
  );
  const clientResponse = yield call(
    http.get,
    `/api/v1/contact-centre/client/${clientId}/absence-recording-configuration`,
  );

  yield all([
    put(actionCreator.SetClient(clientResponse.data)),
    put(actionCreator.SetClientSettings(response.data)),
    put(serviceActionCreator.GetServiceSettings({ clientId })),
    put(sharedActionCreators.GetAbsenceReasons({ clientId })),
    put(actionCreator.GetEmployeeAbsenceRecordingWellbeingService({ clientId })),
    put(
      actionCreator.SetCallPurpose({
        ...payload,
        client: clientResponse.data,
        settings: response.data,
      }),
    ),
    put(actionCreator.SelectClientSuccess()),
  ]);

  return clientResponse.data;
}

type SetCallPurposePayload = $Exact<{
  callerIdentity: string,
  reasonForCall: string,
  noSubmit?: boolean,
}>;

export function* onSetCallPurpose({ payload }: { payload: SetCallPurposePayload }) {
  const { callerIdentity, reasonForCall, noSubmit, client, settings } = payload;

  // here call
  yield put(
    actionCreator.SetCallPurposeSuccess({ callerIdentity, reasonForCall, client, settings }),
  );

  if (!noSubmit) {
    yield put(
      actionCreator.SubmitCurrentStep({
        callerIdentity,
        reasonForCall,
        client,
        settings,
      }),
    );
  }
}

export function* onGetAbsenceRecordingConfig() {
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/contact-centre/absence-recording-configuration`,
    );
    yield put(actionCreator.GetAbsenceRecordingConfigSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetAbsenceRecordingConfigError(e.response));
  }
}

export function* onSelectNextCallback({ payload }) {
  try {
    const response = yield call(http.get, `api/v1/contact-centre/callback-queue/${payload}/next`);
    yield put(actionCreator.selectNextCallbackSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield all([
      put(actionCreator.GetCallbackQueues()),
      put(actionCreator.selectNextCallbackError(e.response)),
    ]);
  }
}

export function* onSearchEmployees({ payload }) {
  const selectedClientId = yield select(selectClientId);
  const clientId = selectedClientId || payload.clientId;

  try {
    const response = yield call(http.get, `api/v1/contact-centre/client/${clientId}/employee`, {
      params: {
        ...payload.filters,
        limit: config.shortResultsPerPage,
        page: payload.page || 1,
      },
    });

    yield put(actionCreator.SearchEmployeesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.SearchEmployeesError(e.response));
  }
}

export function* onGetDataSecuritySettings({ payload }) {
  const clientId = payload.id || null;

  try {
    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${clientId}/service-config/platform/data-security-employee-identity`,
    );

    yield put(actionCreator.GetDataSecuritySettingsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetDataSecuritySettingsError(e.response));
  }
}

export function* onCreateCallInteraction({ payload }) {
  const clientId = payload.clientId || null;
  const formData = payload.formData || null;

  try {
    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${clientId}/call-interaction`,
      formData,
    );

    yield put(actionCreator.CreateCallInteractionSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.CreateCallInteractionError(e.response));
  }
}

export function* onGetCallInteraction({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId) {
      throw new Error("Invalid payload");
    }

    const response = yield call(
      http.get,
      `/api/v2/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}`,
    );

    yield put(actionCreator.GetCallInteractionSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetCallInteractionError(e.response));
  }
}

export function* onUpdateCallInteraction({ payload }) {
  if (!payload || !payload.clientId || !payload.callInteraction || !payload.submitCurrentStep) {
    throw new Error("Invalid payload");
  }
  const clientId = payload.clientId;
  const callInteractionId = payload.callInteraction.id;
  delete payload.callInteraction.id;
  const callInteraction = JSON.parse(JSON.stringify(payload.callInteraction));
  const submitCurrentStep = payload.submitCurrentStep || true;

  delete callInteraction.interactionId;

  try {
    const response = yield call(
      http.put,
      `/api/v1/contact-centre/client/${clientId}/call-interaction/${callInteractionId}`,
      callInteraction,
    );

    yield put(actionCreator.UpdateCallInteractionSuccess(response.data));
    if (submitCurrentStep) {
      yield put(actionCreator.SubmitCurrentStep(response.data));
    }
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.UpdateCallInteractionError(e.response));
  }
}

export function* onAssignAbsenceLanding({ payload }) {
  const clientId = payload.clientId || null;
  const callInteractionId = payload.callInteractionId || null;
  const formData = payload.formData || null;

  try {
    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${clientId}/call-interaction/${callInteractionId}/absence-landing`,
      formData,
    );

    yield put(actionCreator.AssignAbsenceLandingSuccess(response.data));

    // when open new absence we need to retrieve the list of absence
    if (payload.employeeId) {
      yield put(
        actionCreator.GetEmployeeAbsenceRecording({ clientId, employeeId: payload.employeeId }),
      );
    }

    yield put(actionCreator.SubmitCurrentStep(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.AssignAbsenceLandingError(e.response));
  }
}

type AssignCallbackLandingRequest = $Exact<{
  clientId: number,
  callInteractionId: number,
  callback: Object,
}>;

export function* onAssignCallbackLanding({
  payload,
}: Action<AssignCallbackLandingRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId || !payload.callback) {
      throw new Error("Invalid payload");
    }

    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/absence-landing`,
      {
        absenceType: payload.callback.absence.absenceType,
        absenceStartsAt: payload.callback.absence.startsAt,
        contactPhoneNumber: payload.callback.absence.contactPhoneNumber,
        absenceId: payload.callback.absence.id,
      },
    );

    yield put(actionCreator.AssignCallbackLandingSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.AssignCallbackLandingError((e || {}).response));
  }
}

type AssignTransferLandingRequest = $Exact<{
  clientId: number,
  callInteractionId: number,
  transfer: Object,
}>;

export function* onAssignTransferLanding({
  payload,
}: Action<AssignTransferLandingRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId || !payload.transfer) {
      throw new Error("Invalid payload");
    }

    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/absence-landing`,
      {
        absenceType: payload.transfer.absence.absenceType,
        absenceStartsAt: payload.transfer.absence.startsAt,
        contactPhoneNumber: payload.transfer.absence.contactPhoneNumber,
        absenceId: payload.transfer.absence.id,
      },
    );

    yield put(actionCreator.AssignTransferLandingSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.AssignTransferLandingError((e || {}).response));
  }
}

export function* onGetEmployeeAbsenceRecording({ payload }) {
  const clientId = payload.clientId || null;
  const employeeId = payload.employeeId || null;
  const userType = yield select(getUserType);

  try {
    const response = yield call(
      http.get,
      `/api/v1/contact-centre/client/${clientId}/employee/${employeeId}/employee-absence-recording`,
    );
    const data = {
      ...response.data,
    };

    yield put(actionCreator.GetEmployeeAbsenceRecordingSuccess(data));

    // update recording "selectedAbsence" state
    if (payload.absenceId) {
      const newAbsence = data.absences.find(item => item.id === payload.absenceId);

      if (newAbsence) {
        const wizardState = yield select(selectWizardState);

        yield all([
          yield put(actionCreator.SetCallInteractionAbsence(newAbsence)),
          yield put(
            actionCreator.SetAbsenceRecordingWizardState([
              ...wizardState.map((item, index) =>
                index === 10
                  ? {
                      ...item,
                      absence: newAbsence,
                    }
                  : item,
              ),
            ]),
          ),
        ]);
      }
    }

    if (payload.nurseCallback && userType !== USER_TYPES.ABSENCE_ADVISOR) {
      yield put(
        actionCreator.GetMedicalTriageHistory({
          clientId,
          employeeId,
        }),
      );
    }
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetEmployeeAbsenceRecordingError(e.response));
  }
}
export function* onGetEmployeeAbsenceRecordingWellbeingService({ payload }) {
  const clientId = payload.clientId || null;

  try {
    const eapDetails = yield call(
      http.get,
      `/api/v2/service-admin/client/${clientId}/service-config/absence-management/wellbeing/service`,
    );

    yield put(
      actionCreator.GetEmployeeAbsenceRecordingWellbeingServiceSuccess(eapDetails.data.content),
    );
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetEmployeeAbsenceRecordingWellbeingServiceError(e.response));
  }
}

export function* onGetCallbackQueues() {
  try {
    const response = yield call(http.get, `api/v1/contact-centre/callback-queue`);
    yield put(actionCreator.getCallbackQueuesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.getCallbackQueuesError(e.response));
  }
}

export function* onUpdateCallback({ payload }) {
  const callbackId = payload.id;

  try {
    const response = yield call(http.put, `api/v1/contact-centre/callback/${callbackId}`, payload);
    yield all([
      put(actionCreator.updateCallbackSuccess(response.data)),
      put(actionCreator.getCallbackQueues()),
    ]);

    switch (payload.status) {
      case "REMOVED":
        yield put(sendSuccessMessage("CALLBACK_REMOVED"));
        break;
      case "FAILED":
        yield put(sendSuccessMessage("CALLBACK_UPDATED"));
        break;
      default:
    }
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.updateCallbackError(e.response));
  }
}

export function* onRestoreCallback({ payload }) {
  const callbackId = payload.id;

  try {
    const response = yield call(
      http.put,
      `api/v1/contact-centre/callback/${callbackId}`,
      payload.data,
    );
    yield all([
      put(actionCreator.updateCallbackSuccess(response.data)),
      put(sendSuccessMessage("CALLBACK_UPDATED")),
      put(actionCreator.getCallbackQueues()),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.updateCallbackError(e.response));
  }
}

type AcceptNurseCallback = $Exact<{
  id: number,
  client: {
    id: number,
  },
  employee: {
    id: number,
  },
}>;

export function* onAcceptNurseCallback({
  payload,
}: Action<AcceptNurseCallback>): Generator<*, *, *> {
  try {
    if (!payload) {
      throw Error("Invalid payload provided");
    }

    const callbackId: number = payload.id;
    const clientId: number = payload.client.id;
    const employeeId: number = payload.employee.id;
    const callerIdentity: string = "SELF";
    const reasonForCall: string = "START_UPDATE_CLOSE_ABSENCE";

    const { data: settings } = yield call(
      http.get,
      `/api/v2/service-admin/client/${clientId}/service-config/platform/data-security-employee-identity`,
    );
    const { data: client } = yield call(
      http.get,
      `/api/v1/contact-centre/client/${clientId}/absence-recording-configuration`,
    );

    const { data: employee } = yield call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}`,
    );
    const employeeSchema = yield parseSchema("EmployeeDTO", { clientId });
    yield put(push(RECORDING_PATH));

    yield put(
      actionCreator.GetEmployeeAbsenceRecording({ clientId, employeeId, nurseCallback: true }),
    );

    yield put(serviceActionCreator.GetServiceSettings({ clientId }));

    yield call(http.put, `/api/v1/contact-centre/callback/${callbackId}`, {
      id: callbackId,
      status: "SUCCESS",
    });

    yield call(delay, 500);

    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${clientId}/call-interaction`,
      {
        employeeId,
        callerIdentity,
        reasonForCall,
        interactionSource: "CALLBACK",
        callbackId,
      },
    );

    yield all([
      put(actionCreator.SetClient(client)),
      put(actionCreator.SetSettings(settings)),
      put(actionCreator.SetEmployee(employee)),
      put(actionCreator.SetEmployeeSchema(employeeSchema)),
      put(actionCreator.AcceptNurseCallbackSuccess(response.data)),
      put(actionCreator.ToggleNurseCallbackModal(true)),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.AcceptNurseCallbackError((e || {}).response));
  }
}

export function* onGetCallbackScript() {
  try {
    const response = yield call(http.get, `/api/v1/contact-centre/call-script`, {
      params: {
        search: "key_name:callback",
      },
    });

    yield put(actionCreator.GetCallbackScriptSuccess(response.data.content[0]));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetCallbackScriptError(e.response));
  }
}

export function* onCreateReasonAndDuration({ payload }) {
  const clientId = payload.clientId;
  const callInteractionId = payload.callInteractionId;
  const formData = payload.formData;

  try {
    const { data: reasonAndDuration } = yield call(
      http.post,
      `/api/v1/contact-centre/client/${clientId}/call-interaction/${callInteractionId}/absence-reason-and-duration`,
      formData,
    );

    if (reasonAndDuration.timeLost && reasonAndDuration.timeLost.length) {
      yield put(actionCreator.SetUpdateShiftPatternTimeLost(reasonAndDuration.timeLost));
    }

    yield all([
      put(actionCreator.CreateReasonAndDurationSuccess(reasonAndDuration)),
      put(actionCreator.SubmitCurrentStep({ reasonAndDuration })),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.CreateReasonAndDurationError(e.response));
  }
}

export function* onCreateQuery({ payload }) {
  const clientId = payload.clientId;
  const callInteractionId = payload.callInteractionId;

  const formData = {
    queryText: payload.formData.queryText,
    queryType: {
      id: payload.formData.queryType,
    },
  };

  try {
    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${clientId}/call-interaction/${callInteractionId}/query`,
      formData,
    );

    yield all([
      put(actionCreator.CreateQuerySuccess(response.data)),
      put(actionCreator.SubmitCurrentStep({ query: response.data })),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.CreateQeuryError(e.response));
  }
}

export function* onCancelCallInteraction({ payload }) {
  const clientId = payload.clientId;
  const callInteractionId = payload.callInteractionId;

  try {
    yield call(
      http.post,
      `/api/v1/contact-centre/client/${clientId}/call-interaction/${callInteractionId}/cancel`,
    );

    yield put(actionCreator.CancelCallInteractionSuccess());
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.CancelCallInteractionError(e.response));
  }
}

export function* onGetAbsenceReasonAndDurationSchema({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const schema = yield parseSchema("AbsenceCallReasonAndDurationDTO", {
      clientId: payload.clientId,
      absenceReasonType: payload.absenceReasonType,
    });

    yield put(actionCreator.GetAbsenceReasonAndDurationSchemaSuccess(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetAbsenceReasonAndDurationSchemaError(e.response));
  }
}

export function* onGetAbsenceLandingSchema({ payload }) {
  const clientId = payload.clientId;

  try {
    const schema = yield parseSchema("AbsenceCallLandingDTO", { clientId });
    yield put(actionCreator.GetAbsenceLandingSchemaSuccess(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetAbsenceLandingSchemaError(e));
  }
}

export function* onGetEmployeeSchema({ payload }) {
  const clientId = payload.clientId || null;
  try {
    const schema = yield parseSchema("EmployeeDTO", { clientId });
    yield put(actionCreator.GetEmployeeSchemaSuccess(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetEmployeeSchemaError(e.response));
  }
}

export function* onGetInteractionNotesSchema({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const schema = yield parseSchema("CallInteractionAdvisorNotesDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreator.GetInteractionNotesSchemaSuccess(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetInteractionNotesSchemaError(e.response));
  }
}

export function* onAssignCallInteractionNotes({ payload }: Object) {
  try {
    const clientId = yield select(selectClientId);
    const callInteraction = yield select(selectCallInteraction);

    if (!clientId || !callInteraction) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/contact-centre/client/${clientId}/call-interaction/${callInteraction.id ||
        callInteraction.callInteraction.id}/call-interaction-notes`,
      payload,
    );

    yield all([
      put(actionCreator.AssignCallInteractionNotesSuccess()),
      put(sendSuccessMessage("INTERACTION_SAVED")),
      put(actionCreator.SubmitCurrentStep(payload)),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.AssignCallInteractionNotesError(e.response));
  }
}

export function* onGetEmployeePositions({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position`,
    );

    yield put(actionCreator.GetEmployeePositionsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetEmployeePositionsError(e.response));
  }
}

export function* onGetAbsenceTimeLostSchema({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const schema = yield parseSchema("AbsenceTimeLostDTO", { clientId: payload.clientId });

    yield put(actionCreator.GetAbsenceTimeLostSchemaSuccess(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetAbsenceTimeLostSchemaError(e.response));
  }
}

export function* onAssignAbsenceTimeLost({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.absenceId || !payload.timeLost) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.absenceId}/absence-time-lost`,
      payload.timeLost,
    );

    yield all([
      put(actionCreator.AssignAbsenceTimeLostSuccess(response.data)),

      put(
        actionCreator.GetCallInteractionReferralQuestions({
          clientId: payload.clientId,
          callInteractionId: payload.absenceId,
        }),
      ),
      put(
        actionCreator.GetCallInteractionDynamicQuestions({
          clientId: payload.clientId,
          callInteractionId: payload.absenceId,
        }),
      ),
      put(
        actionCreator.GetCallInteractionStandardQuestions({
          clientId: payload.clientId,
          callInteractionId: payload.absenceId,
        }),
      ),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.AssignAbsenceTimeLostError(e.response));
  }
}

export function* onConfirmCallInteraction({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/absence-details/confirm`,
    );

    yield put(actionCreator.ConfirmCallInteractionSuccess(response.data));

    // when reopen and update the absence, it need to refresh the absence list data
    if (payload.employeeId) {
      yield put(
        actionCreator.GetEmployeeAbsenceRecording({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: response.data.absenceId,
        }),
      );
    }
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.ConfirmCallInteractionError(e.response));
  }
}

export function* onCloseAbsence({ payload }) {
  try {
    if (!payload.clientId || !payload.callInteractionId || !payload.absenceId) {
      throw Error("Invalid payload provided");
    }

    const { data } = yield call(
      http.post,
      `api/v2/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/absence/${payload.absenceId}/close`,
    );

    if (payload.additionalInfo) {
      yield put(actionCreator.AddAdditionalInfo(payload.additionalInfo));
    }
    yield put(actionCreator.AssignAbsenceLandingSuccess(data.landing));
    yield put(actionCreator.CloseAbsenceSuccess());
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.CloseAbsenceError(e.response));
  }
}

export function* onGetCallInteractionDynamicQuestions({ payload }) {
  try {
    if (!payload.clientId || !payload.callInteractionId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/dynamic-question`,
    );

    yield parseSchema("AbsenceDynamicQuestionAnswerDTO", { clientId: payload.clientId });
    yield put(actionCreator.GetCallInteractionDynamicQuestionsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetCallInteractionDynamicQuestionsError(e.response));
  }
}

export function* onGetCallInteractionReferralQuestions({ payload }) {
  try {
    const { clientId, callInteractionId } = payload;
    if (!clientId || !callInteractionId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/contact-centre/client/${clientId}/call-interaction/${callInteractionId}/employee-referral-tasks`,
    );
    yield put(actionCreator.GetCallInteractionReferralQuestionsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetCallInteractionReferralQuestionsError(e.response));
  }
}

export function* onPutCallInteractionReferral({ payload }) {
  try {
    const { clientId, callInteractionId, employeeReferralTaskId, referral } = payload;
    const response = yield call(
      http.put,
      `${config.endpointBaseUrl}api/v2/contact-centre/client/${clientId}/call-interaction/${callInteractionId}/employee-referral-tasks/${employeeReferralTaskId}`,
      referral,
    );
    yield put(actionCreator.PutCallInteractionReferralQuestionsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetCallInteractionReferralQuestionsError(e.response));
  }
}

export function* onGetCallInteractionStandardQuestions({ payload }) {
  try {
    if (!payload.clientId || !payload.callInteractionId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/standard-question`,
    );
    yield put(actionCreator.GetCallInteractionStandardQuestionsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.GetCallInteractionStandardQuestionsError(e.response));
  }
}

export function* onAssignStandardQuestionAnswers({ payload }) {
  try {
    if (!payload.clientId || !payload.callInteractionId || !payload.formData) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v2/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/standard-question-answers`,
      payload.formData,
    );

    yield put(actionCreator.AssignStandardQuestionAnswersSuccess(response.data));

    if (payload.nextDynamicPayload) {
      yield put(actionCreator.AssignDynamicQuestionAnswers(payload.nextDynamicPayload));
    } else {
      yield put(actionCreator.SubmitCurrentStep(response.data));
    }
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.AssignStandardQuestionAnswersError(e.response));
  }
}

export function* onAssignDynamicQuestionAnswers({ payload }) {
  try {
    if (!payload.clientId || !payload.callInteractionId || !payload.formData) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v2/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/dynamic-question-answers`,
      payload.formData,
    );

    yield put(actionCreator.AssignDynamicQuestionAnswersSuccess(response.data));
    if (payload.shouldSubmitStep) {
      yield put(actionCreator.SubmitCurrentStep(response.data));
    }
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.AssignDynamicQuestionAnswersError(e.response));
  }
}

export function* onCreateMedicalAdviceCallback({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  try {
    if (!payload) {
      throw Error("Invalid payload provided");
    }

    yield call(http.post, `/api/v1/contact-centre/callback`, payload);

    yield all([
      put(actionCreator.CreateMedicalAdviceCallbackSuccess()),
      put(sendSuccessMessage("CALLBACK_ADDED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreator.CreateMedicalAdviceCallbackError(e.response));
  }
}

export function* onGetSettingTheSceneText({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId || !payload.medicalTriageId) {
      throw Error("Invalid payload provided");
    }

    const res = yield call(
      http.get,
      `api/v1/medical-triage/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/medical-triage/${payload.medicalTriageId}/setting-the-scene`,
    );

    yield put(actionCreator.GetSettingTheSceneTextSuccess(res));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(
      actionCreator.GetSettingTheSceneTextError(
        e.response || `Something went wrong in onGetSettingTheSceneText`,
      ),
    );
  }
}

export function* onPostSettingTheSceneText({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId || !payload.medicalTriageId) {
      throw Error("Invalid payload provided");
    }

    const res = yield call(
      http.post,
      `api/v1/medical-triage/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/medical-triage/${payload.medicalTriageId}/setting-the-scene`,
      payload.data,
    );

    yield put(actionCreator.PostSettingTheSceneTextSuccess(res.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(
      actionCreator.PostSettingTheSceneTextError(
        e.response || `Something went wrong in onPostSettingTheSceneText`,
      ),
    );
  }
}

export function* onPutSettingTheSceneText({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId || !payload.medicalTriageId) {
      throw Error("Invalid payload provided");
    }

    const res = yield call(
      http.put,
      `api/v1/medical-triage/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/medical-triage/${payload.medicalTriageId}/setting-the-scene`,
      payload.data,
    );

    yield put(actionCreator.PostSettingTheSceneTextSuccess(res.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(
      actionCreator.PostSettingTheSceneTextError(
        e.response || `Something went wrong in onPostSettingTheSceneText`,
      ),
    );
  }
}

export function* onCreateNewAbsence({ payload }: Action<Object>): Generator<void, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.daysLost || !payload.interactionId) {
      throw Error("Invalid payload provided");
    }

    const absenceData = yield select(state => state.recording.reasonAndDuration);

    const data = {
      daysLost: payload.daysLost,
      ...absenceData,
      startsAt: absenceData.absenceStartsAt,
      consentToShare: absenceData.hasConsentToShare,
    };

    delete data.employeeId;
    delete data.absenceStartsAt;
    delete data.hasConsentToShare;
    delete data.isCloseable;

    const response = yield call(
      http.post,
      `api/v2/contact-centre/client/${payload.clientId}/call-interaction/${payload.interactionId}/absence`,
      data,
    );

    yield all([
      put(
        actionCreator.GetCallInteractionReferralQuestions({
          clientId: payload.clientId,
          callInteractionId: payload.interactionId,
        }),
      ),
      put(
        actionCreator.GetCallInteractionDynamicQuestions({
          clientId: payload.clientId,
          callInteractionId: payload.interactionId,
        }),
      ),
      put(
        actionCreator.GetCallInteractionStandardQuestions({
          clientId: payload.clientId,
          callInteractionId: payload.interactionId,
        }),
      ),
      put(
        actionCreator.GetEmployeeAbsenceRecording({
          clientId: payload.clientId,
          employeeId: absenceData.employeeId,
        }),
      ),
    ]);

    yield put(actionCreator.CreateNewAbsenceSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(
      actionCreator.CreateNewAbsenceError(
        e.response || `Something went wrong in onCreateNewAbsence`,
      ),
    );
  }
}

export function* onUpdateAbsence({ payload }: Action<Object>): Generator<void, void, Object> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.daysLost ||
      !payload.interactionId ||
      !payload.absenceId
    ) {
      throw Error("Invalid payload provided");
    }

    const absenceData = yield select(state => state.recording.reasonAndDuration);

    const data = {
      id: payload.absenceId,
      daysLost: payload.daysLost,
      ...absenceData,
      startsAt: moment(absenceData.absenceStartsAt).format(config.dateTimeFormatBE),
      fitAt: moment(absenceData.fitAt).format(config.dateTimeFormatBE),
      rtwAt: moment(absenceData.rtwAt).format(config.dateTimeFormatBE),
      consentToShare: absenceData.hasConsentToShare,
    };

    delete data.employeeId;
    delete data.absenceStartsAt;
    delete data.hasConsentToShare;
    delete data.isCloseable;

    const response = yield call(
      http.put,
      `api/v2/contact-centre/client/${payload.clientId}/call-interaction/${payload.interactionId}/absence/${payload.absenceId}`,
      data,
    );

    yield all([
      put(
        actionCreator.GetCallInteractionReferralQuestions({
          clientId: payload.clientId,
          callInteractionId: payload.interactionId,
        }),
      ),
      put(
        actionCreator.GetCallInteractionDynamicQuestions({
          clientId: payload.clientId,
          callInteractionId: payload.interactionId,
        }),
      ),
      put(
        actionCreator.GetCallInteractionStandardQuestions({
          clientId: payload.clientId,
          callInteractionId: payload.interactionId,
        }),
      ),
    ]);

    yield put(actionCreator.UpdateExistingAbsenceSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(
      actionCreator.UpdateExistingAbsenceError(
        e.response || `Something went wrong in onUpdateAbsence`,
      ),
    );
  }
}

export function* onAddAdditionalInfo({ payload }: Action<Object>): Generator<void, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/additional-info`,
      payload.data,
    );

    yield put(actionCreator.PostSettingTheSceneTextSuccess(response.data));
    if (payload.shouldSubmitStep) {
      yield put(actionCreator.SubmitCurrentStep(response.data));
    }
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(
      actionCreator.PostSettingTheSceneTextError(
        e.response || `Something went wrong in onAddAdditionalInfo`,
      ),
    );
  }
}

export function* onGetMedicalTriageHistory({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/medical-triage/client/${payload.clientId}/employee/${payload.employeeId}/medical-triages-history`,
      {
        params: {
          limit: config.resultsPerPage,
          page: payload.page || 1,
        },
      },
    );

    yield put(actionCreator.GetMedicalTriageHistorySuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(
      actionCreator.GetMedicalTriageHistoryError(
        e.response || `Something went wrong in onGetMedicalTriageHistory`,
      ),
    );
  }
}

export function* onGetMedicalTriageDetails({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId || !payload.medicalTriageId) {
      throw Error("Invalid payload provided");
    }

    const assessmentResponse = yield call(
      http.get,
      `/api/v2/medical-triage/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/medical-triage/${payload.medicalTriageId}/medical-assessment`,
    );

    const sceneResponse = yield call(
      http.get,
      `/api/v1/medical-triage/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/medical-triage/${payload.medicalTriageId}/setting-the-scene`,
    );

    yield put(
      actionCreator.GetMedicalTriageDetailsSuccess({
        assesment: assessmentResponse.data,
        scene: sceneResponse.data,
      }),
    );
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(
      actionCreator.GetMedicalTriageDetailsError(
        e.response || `Something went wrong in onGetMedicalTriageHistory`,
      ),
    );
  }
}

export function* onGetJobDuties({ payload }: Action<Object>): Generator<void, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/contact-centre/client/${payload.clientId}/employee/${payload.employeeId}/job-duty/responses`,
    );

    yield put(actionCreator.GetJobDutiesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(
      actionCreator.GetJobDutiesError(
        e.response || `Something went wrong in onGetMedicalTriageHistory`,
      ),
    );
  }
}

export function* onGetQuestionsAndAnswers({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.callInteractionId) {
      throw Error("Invalid payload provided");
    }

    const dynamicResponse = yield call(
      http.get,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/dynamic-question`,
    );
    const standardResponse = yield call(
      http.get,
      `/api/v1/contact-centre/client/${payload.clientId}/call-interaction/${payload.callInteractionId}/standard-question`,
    );

    yield put(
      actionCreator.GetQuestionsAndAnswersSuccess({
        dynamic: dynamicResponse.data,
        standard: standardResponse.data,
      }),
    );
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(
      actionCreator.GetQuestionsAndAnswersError(
        e.response || `Something went wrong in onGetQuestionsAndAnswers`,
      ),
    );
  }
}

export function* RecordingSaga() {
  yield all([
    spawn(triage),
    spawn(nurseTransferSagas),
    spawn(clientInformation),

    takeEvery(actions.GetAbsenceRecordingConfig, onGetAbsenceRecordingConfig),
    takeEvery(actions.GetCallbackQueues, onGetCallbackQueues),
    takeEvery(actions.SelectNextCallback, onSelectNextCallback),
    takeEvery(actions.UpdateCallback, onUpdateCallback),
    takeEvery(actions.RestoreCallback, onRestoreCallback),
    takeEvery(actions.AcceptNurseCallback, onAcceptNurseCallback),
    takeEvery(actions.GetCallbackScript, onGetCallbackScript),
    takeLatest(actions.CreateCallInteraction, onCreateCallInteraction),
    takeEvery(actions.GetCallInteraction, onGetCallInteraction),
    takeEvery(actions.SearchEmployees, onSearchEmployees),
    takeEvery(actions.AbsenceRecordingSelectEmployee, onAbsenceRecordingSelectEmployee),
    takeEvery(actions.GetPreselectedEmployee, onGetPreselectedEmployee),
    takeEvery(actions.SetCallPurpose, onSetCallPurpose),
    takeEvery(actions.GetEmployeeSchema, onGetEmployeeSchema),
    takeEvery(actions.GetInteractionNotesSchema, onGetInteractionNotesSchema),
    takeEvery(actions.GetEmployeeAbsenceRecording, onGetEmployeeAbsenceRecording),
    takeEvery(
      actions.GetEmployeeAbsenceRecordingWellbeingService,
      onGetEmployeeAbsenceRecordingWellbeingService,
    ),
    takeLatest(actions.UpdateCallInteraction, onUpdateCallInteraction),
    takeLatest(actions.AssignAbsenceLanding, onAssignAbsenceLanding),
    takeEvery(actions.AssignCallbackLanding, onAssignCallbackLanding),
    takeEvery(actions.AssignTransferLanding, onAssignTransferLanding),
    takeEvery(actions.CreateReasonAndDuration, onCreateReasonAndDuration),
    takeEvery(actions.CreateQuery, onCreateQuery),
    takeEvery(actions.GetAbsenceReasonAndDurationSchema, onGetAbsenceReasonAndDurationSchema),
    takeEvery(actions.GetAbsenceLandingSchema, onGetAbsenceLandingSchema),
    takeEvery(actions.AssignCallInteractionNotes, onAssignCallInteractionNotes),
    takeEvery(actions.GetEmployeePositions, onGetEmployeePositions),
    takeEvery(actions.GetAbsenceTimeLostSchema, onGetAbsenceTimeLostSchema),
    takeEvery(actions.AssignAbsenceTimeLost, onAssignAbsenceTimeLost),
    takeEvery(actions.CancelCallInteraction, onCancelCallInteraction),
    takeEvery(actions.ConfirmCallInteraction, onConfirmCallInteraction),
    takeEvery(actions.SelectClient, onSelectClient),
    takeEvery(actions.CloseAbsence, onCloseAbsence),
    takeEvery(actions.GetCallInteractionDynamicQuestions, onGetCallInteractionDynamicQuestions),
    takeEvery(actions.GetCallInteractionReferralQuestions, onGetCallInteractionReferralQuestions),
    takeEvery(actions.PutCallInteractionReferral, onPutCallInteractionReferral),
    takeEvery(actions.GetCallInteractionStandardQuestions, onGetCallInteractionStandardQuestions),
    takeEvery(actions.AssignStandardQuestionAnswers, onAssignStandardQuestionAnswers),
    takeEvery(actions.AssignDynamicQuestionAnswers, onAssignDynamicQuestionAnswers),
    takeEvery(actions.CreateMedicalAdviceCallback, onCreateMedicalAdviceCallback),
    takeEvery(actions.PostSettingTheSceneText, onPostSettingTheSceneText),
    takeEvery(actions.PutSettingTheSceneText, onPutSettingTheSceneText),
    takeEvery(actions.GetSettingTheSceneText, onGetSettingTheSceneText),
    takeEvery(actions.CreateNewAbsence, onCreateNewAbsence),
    takeEvery(actions.UpdateExistingAbsence, onUpdateAbsence),
    takeEvery(actions.AddAdditionalInfo, onAddAdditionalInfo),
    takeEvery(actions.GetMedicalTriageHistory, onGetMedicalTriageHistory),
    takeEvery(actions.GetMedicalTriageDetails, onGetMedicalTriageDetails),
    takeEvery(actions.GetJobDuties, onGetJobDuties),
    takeEvery(actions.GetQuestionsAndAnswers, onGetQuestionsAndAnswers),
  ]);
}

export default RecordingSaga;
